import { Link } from "wouter";
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  BookOpen,
  ClipboardList,
  Compass,
  Database,
  FlaskConical,
  Gauge,
  Layers,
  Microscope,
  Save,
  Search,
} from "lucide-react";

import { Button } from "@/components/ui/button";

interface Step {
  num: number;
  icon: typeof Search;
  title: string;
  intro: string;
  bullets: string[];
  caveat?: string;
}

const STEPS: Step[] = [
  {
    num: 1,
    icon: Search,
    title: "Start with a query",
    intro:
      "The Query page is the front door for every analysis. Inputs are flexible — you can mix identifier types in a single search.",
    bullets: [
      "Accepted inputs: gene symbols (TP53), protein names (Tumor Protein P53), or UniProt accessions (P04637).",
      "Multi-input is supported. Paste a list separated by commas, spaces, or newlines.",
      "Choose a species — 8 model organisms are available, including human, mouse, rat, and zebrafish.",
      "Use an Example Query chip if you want to see the workflow before bringing in your own list.",
    ],
  },
  {
    num: 2,
    icon: Compass,
    title: "Add biological context",
    intro:
      "Biological context is one of ACADEM's main differentiators. It is exposed as a first-class control, not a hidden filter.",
    bullets: [
      "On the Query page, click a Quick Pathology Panel preset to populate genes, tissue, cell type, and disease mode in one click.",
      "On the Results page, refine context independently in the sidebar: Tissue, Cell type, Disease mode, and partner-gene search.",
      "Active context is rendered as a labelled banner above the results — you always know which context shaped the ranking.",
      "Context modifies scoring weights and assay recommendations. It is never silently applied in the background.",
    ],
  },
  {
    num: 3,
    icon: Gauge,
    title: "Read the rankings correctly",
    intro:
      "Rank is a prioritization signal — a triage hint, not proof. Channels and tiers tell you how much weight to put on each candidate.",
    bullets: [
      "The rank circle colour reflects the priority band (green ≥ 0.6, amber 0.4–0.6, grey < 0.4).",
      "The Channels mini-bars show which STRING channels are active. Hover any segment for its 0–1000 score.",
      "Evidence-tier badge tells you the strongest type of support — experimental and curated_db are direct evidence; textmining and coexpression are not binding evidence.",
      "Uncertainty pill (low / moderate / high) blends channel count, presence of direct evidence, and conservation breadth.",
    ],
    caveat:
      "Channels are not biologically equivalent. AlphaFold support is structural plausibility. Open Targets scores are aggregated relevance, not mechanism.",
  },
  {
    num: 4,
    icon: Microscope,
    title: "Open the detail drawer",
    intro:
      "Click any interaction row to open the right-side drawer. The drawer is the interpretation surface — every section is built to support a decision.",
    bullets: [
      "Why is this interaction here? — plain-language summary of the dominant evidence and the channels that drove the score.",
      "What is missing — explicit gaps (no Open Targets disease relevance, no AlphaFold complex, etc.) surfaced as first-class info.",
      "What would upgrade confidence — concrete evidence types (PLA, AlphaFold3 complex, GWAS / eQTL) that would raise the tier.",
      "Lab Priority Score — large numeric score with a Show model expander for the 6-factor breakdown.",
      "Assay Recommendations — a tier-aware First assay / Orthogonal / Controls card.",
      "Decision — Validate now / Maybe later / Reject with an optional reason. The choice is logged immediately.",
    ],
  },
  {
    num: 5,
    icon: Save,
    title: "Save decisions and continue later",
    intro:
      "ACADEM is built for repeat workflows. Decisions and saved panels carry across sessions so you can pick up where you left off.",
    bullets: [
      "Decisions persist in the Decision Log tab — filter by action, query, or species.",
      "Save Panel keeps the active query, species, and context attached so you can re-run it later.",
      "Compare genes (visible when a panel has ≥ 2 query genes) partitions partners across Shared / Unique / Hubs / Bridges.",
      "Export the analysis as CSV (with REPR-03 metadata header) or JSON. Decisions export as JSON today; CSV for decisions ships in v1.",
      "Use Refresh data to bypass the cache and re-fetch fresh evidence for the active query.",
    ],
  },
];

const MISTAKES: Array<{ title: string; body: string }> = [
  {
    title: "Treating rank or score as proof",
    body: "Rank is a prioritization signal. The product is explicit: it surfaces hypotheses for testing, not validated interactions.",
  },
  {
    title: "Reading AlphaFold support as binding evidence",
    body: "AlphaFold pLDDT indicates structural plausibility. It must be confirmed by orthogonal experimental methods before mechanistic conclusions.",
  },
  {
    title: "Reading Open Targets scores as mechanism",
    body: "Open Targets disease relevance is aggregated. It does not imply a direct causal or mechanistic link between the interaction and the disease.",
  },
  {
    title: "Skipping the What is missing section",
    body: "Missing evidence is first-class information in ACADEM. If you skip it, you lose the most important context for deciding what to test next.",
  },
  {
    title: "Comparing across panels with mismatched filters",
    body: "Inside the Compare view you can relax filters per-tab, but those changes are local. Don't silently rewrite source panels — that breaks reproducibility.",
  },
];

function StepCard({ step }: { step: Step }) {
  const Icon = step.icon;
  return (
    <article
      id={`step-${step.num}`}
      className="rounded-xl border border-border/80 bg-card p-5 shadow-sm sm:p-6"
    >
      <header className="flex items-start gap-4">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-accent/10 font-display text-base font-semibold text-accent">
          {step.num}
        </span>
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Icon className="h-4 w-4 text-accent" />
            <h3 className="font-display text-lg font-semibold tracking-tight">{step.title}</h3>
          </div>
          <p className="text-sm leading-relaxed text-muted-foreground">{step.intro}</p>
        </div>
      </header>

      <ul className="mt-4 space-y-2">
        {step.bullets.map((b) => (
          <li key={b} className="flex items-start gap-2 text-sm leading-relaxed text-foreground">
            <span className="mt-2 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
            <span>{b}</span>
          </li>
        ))}
      </ul>

      {step.caveat && (
        <div className="caveat-banner mt-4">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <p>
            <strong>Read with care.</strong> {step.caveat}
          </p>
        </div>
      )}
    </article>
  );
}

export function GuidePage() {
  return (
    <div className="mx-auto w-full max-w-4xl px-4 py-10 sm:py-14">
      <header className="space-y-3">
        <Link
          to="/welcome"
          className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to welcome
        </Link>
        <div className="flex items-center gap-2">
          <BookOpen className="h-5 w-5 text-accent" />
          <div className="label-eyebrow">Quick guide</div>
        </div>
        <h1 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">
          A 5-step pattern you can apply to every analysis
        </h1>
        <p className="max-w-2xl text-sm leading-relaxed text-muted-foreground">
          ACADEM is optimised for repeat research workflows, not one-off lookups. This guide walks
          you through the working method &mdash; query, context, evidence interpretation, decision,
          continuity &mdash; in the order you will actually use it.
        </p>
      </header>

      <section className="mt-8 rounded-xl border border-accent/30 bg-accent/[0.06] p-5 sm:p-6">
        <div className="flex items-start gap-3">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-md bg-accent/10 text-accent">
            <FlaskConical className="h-4 w-4" />
          </span>
          <div className="space-y-2">
            <div className="text-sm font-semibold text-foreground">Before you begin</div>
            <ul className="space-y-1.5 text-sm leading-relaxed text-foreground/90">
              <li className="flex items-start gap-2">
                <span className="mt-2 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                <span>
                  ACADEM is a <strong>prioritization</strong> tool, not a validation engine.
                  Wet-lab confirmation is still required.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-2 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                <span>
                  Rankings are a <strong>triage signal</strong>. Use them to decide what to test next,
                  not what is true.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-2 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                <span>
                  Caveats and missing-evidence flags are <strong>first-class information</strong>. The
                  drawer is built around them &mdash; read it.
                </span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      <nav className="mt-8 grid gap-2 rounded-lg border border-border/80 bg-muted/30 p-3 sm:grid-cols-5">
        {STEPS.map((s) => (
          <a
            key={s.num}
            href={`#step-${s.num}`}
            className="flex items-center gap-2 rounded-md px-2.5 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-card hover:text-foreground"
          >
            <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-accent/10 text-[10px] font-semibold tabular-nums text-accent">
              {s.num}
            </span>
            <span className="truncate">{s.title}</span>
          </a>
        ))}
      </nav>

      <section className="mt-6 space-y-4">
        {STEPS.map((s) => (
          <StepCard key={s.num} step={s} />
        ))}
      </section>

      <section className="mt-12 space-y-4">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-[hsl(var(--uncertainty-high))]" />
          <div className="label-eyebrow">Common mistakes to avoid</div>
        </div>
        <h2 className="font-display text-xl font-semibold tracking-tight sm:text-2xl">
          Five interpretation traps to keep in mind
        </h2>
        <div className="grid gap-3 sm:grid-cols-2">
          {MISTAKES.map((m) => (
            <div
              key={m.title}
              className="rounded-lg border border-border/80 bg-card p-4 shadow-sm"
            >
              <div className="flex items-start gap-2">
                <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-md bg-[hsl(var(--uncertainty-high))]/10 text-[hsl(var(--uncertainty-high))]">
                  <AlertTriangle className="h-3.5 w-3.5" />
                </span>
                <div className="space-y-1">
                  <div className="text-sm font-semibold text-foreground">{m.title}</div>
                  <p className="text-xs leading-relaxed text-muted-foreground">{m.body}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-12 grid gap-3 sm:grid-cols-3">
        <div className="rounded-lg border border-border/80 bg-muted/30 p-4 text-xs text-muted-foreground">
          <div className="mb-1 flex items-center gap-2 text-foreground">
            <Database className="h-3.5 w-3.5 text-accent" />
            <span className="font-semibold">Data sources</span>
          </div>
          STRING v12, UniProt 2025, Open Targets Platform, AlphaFold DB 2025, Context KB v1.0.
        </div>
        <div className="rounded-lg border border-border/80 bg-muted/30 p-4 text-xs text-muted-foreground">
          <div className="mb-1 flex items-center gap-2 text-foreground">
            <Layers className="h-3.5 w-3.5 text-accent" />
            <span className="font-semibold">Workflow surfaces</span>
          </div>
          Query &middot; Results (Interactions, Lab Priority, Query Proteins, Decisions) &middot; Compare
          genes &middot; Saved Panels &middot; Decision Log.
        </div>
        <div className="rounded-lg border border-border/80 bg-muted/30 p-4 text-xs text-muted-foreground">
          <div className="mb-1 flex items-center gap-2 text-foreground">
            <ClipboardList className="h-3.5 w-3.5 text-accent" />
            <span className="font-semibold">Exports</span>
          </div>
          CSV with REPR-03 metadata header, JSON of the full analysis envelope, JSON of decisions
          (CSV of decisions ships in v1).
        </div>
      </section>

      <section className="mt-10 flex flex-col items-center gap-4 rounded-xl border border-border/80 bg-card px-6 py-8 text-center shadow-sm">
        <h3 className="font-display text-lg font-semibold">You're set</h3>
        <p className="max-w-xl text-sm leading-relaxed text-muted-foreground">
          Try the workflow on a real query &mdash; the Quick Pathology Panels chips on the Query page
          are the fastest way to see the full loop end to end.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <Link to="/welcome">
            <Button variant="outline" className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to welcome
            </Button>
          </Link>
          <Link to="/">
            <Button className="gap-2 bg-accent text-accent-foreground hover:bg-accent/90">
              <Search className="h-4 w-4" />
              Start a query
              <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
